/**
 * Centralized empty state messages.
 *
 * Two APIs:
 * - `entityNames` + `emptyStateMessages` — scenario-aware messages for EmptyState component
 * - `emptyStates` — legacy constants used by non-empty-state contexts (errors, loading, etc.)
 *
 * @example
 * import { emptyStateMessages, entityNames } from '@/constants/emptyStates';
 *
 * <EmptyState message={emptyStateMessages.searched(entityNames.contacts, query)} />
 * <EmptyState message={emptyStateMessages.empty(entityNames.packets)} />
 */

/** Entity name map — always use these, never construct entity strings manually */
export const entityNames = {
  packets: 'packets',
  contacts: 'contacts',
  checklists: 'checklists',
  assignments: 'assignments',
  submissions: 'submissions',
  labels: 'labels',
  notifications: 'notifications',
  questions: 'questions',
  components: 'components',
  integrations: 'integrations',
  mappings: 'field mappings',
  attributes: 'attributes',
} as const;

/** Scenario-aware message builders for EmptyState component */
export const emptyStateMessages = {
  /** User typed a search query that returned 0 results */
  searched: (entity: string, query: string) =>
    `No ${entity} match "${query}"`,

  /** User applied filters that returned 0 results */
  filtered: (entity: string) =>
    `No ${entity} match the current filters`,

  /** Both search and filters active, 0 results */
  searchedAndFiltered: (entity: string, query: string) =>
    `No ${entity} match "${query}" with the current filters`,

  /** No filters, no search — genuinely zero items */
  empty: (entity: string) =>
    `No ${entity} yet`,

  labels: {
    sidebar: 'No labels yet',
  },

  dashboard: {
    recentEmpty: (entity: string) => `No recent ${entity}`,
  },

  integrations: {
    noLabels: 'No label sources mapped. Click a field on the left to add one.',
    noAttributes: 'No attributes mapped. Click a field on the left to add one.',
    configureMappings: 'Configure mappings to see a preview.',
  },
} as const;

/**
 * Helper to capitalize first letter
 */
function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * Helper to pluralize entity names. Idempotent for already-plural input —
 * "contacts" stays "contacts", not "contactses".
 *
 * Limitation: singular nouns that end in -s (e.g. "kiss", "bus") are treated
 * as already plural. Entity names in this app are always passed as plurals
 * (see `entityNames`) so this is acceptable; don't use the helper for
 * arbitrary English nouns.
 */
function pluralize(word: string, count?: number): string {
  if (count === 1) return word;
  if (word === 'person') return 'people';
  if (/s$/.test(word)) return word;
  if (/[xz]$/.test(word) || /[cs]h$/.test(word)) {
    return `${word}es`;
  }
  return `${word}s`;
}

export const emptyStates = {
  /**
   * List empty states - for collections of items
   */
  list: {
    /** When search/filter returns no results */
    noResults: (entityType: string, searchTerm?: string) =>
      searchTerm
        ? `No ${pluralize(entityType)} found for "${searchTerm}"`
        : `No ${pluralize(entityType)} match the current filters`,

    /** When list is genuinely empty (no items exist) */
    empty: (entityType: string) => `No ${pluralize(entityType)} yet`,

    /** Empty with specific context */
    emptyIn: (entityType: string, context: string) =>
      `No ${pluralize(entityType)} in ${context}`,

    /** Call to action for empty list */
    createFirst: (entityType: string) =>
      `Create your first ${entityType} to get started.`,

    /** When archived items are hidden */
    allArchived: (entityType: string) =>
      `All ${pluralize(entityType)} are archived. Show archived items to see them.`,

    /** When filtered by label with no results */
    noMatchingLabels: (entityType: string) =>
      `No ${pluralize(entityType)} have the selected labels.`,

    /** When filtered by status with no results */
    noMatchingStatus: (entityType: string, status: string) =>
      `No ${pluralize(entityType)} are ${status}.`,

    /** Generic filtered state */
    filtered: (entityType: string) =>
      `No ${pluralize(entityType)} match the current filters. Try adjusting your filters.`,

    /** Loading more items */
    loadingMore: 'Loading more...',

    /** End of list */
    endOfList: 'No more items to load.',
  },

  /**
   * Selection states - for multi-select interfaces
   */
  selection: {
    /** When no items are selected for bulk action */
    noneSelected: 'No items selected',

    /** Prompt to select items */
    selectToAction: (action: string) => `Select items to ${action}`,

    /** Count of selected items */
    selectedCount: (count: number, entityType: string) =>
      `${count} ${pluralize(entityType, count)} selected`,

    /** All items selected */
    allSelected: (count: number, entityType: string) =>
      `All ${count} ${pluralize(entityType, count)} selected`,
  },

  /**
   * Detail panel states - for detail views and panels
   */
  detail: {
    /** When detail panel has no content selected */
    selectItem: (entityType: string) =>
      `Select a ${entityType} to view details`,

    /** When item has no sub-items */
    noSubItems: (subType: string) => `No ${pluralize(subType)} added yet`,

    /** When item has no activity */
    noActivity: 'No activity to display',

    /** When item has no history */
    noHistory: 'No history available',

    /** When item has no assignments */
    noAssignments: 'No assignments',

    /** When item has no assignments (search/filter context) */
    noAssignmentsFound: 'No assignments found',

    /** When item has no submissions */
    noSubmissions: 'No submissions yet',

    /** When item has no submissions (search/filter context) */
    noSubmissionsFound: 'No submissions found',

    /** When item has no labels */
    noLabels: 'No labels assigned',

    /** When item has no comments */
    noComments: 'No comments yet',

    /** When item has no attachments */
    noAttachments: 'No attachments',

    /** When document content is not available */
    noDocumentAvailable: 'No document available',
  },

  /**
   * Error states - for failed operations
   */
  error: {
    /** Generic load failure */
    loadFailed: 'Failed to load data. Please try again.',

    /** Q-506 card pattern-2 (Q-509 a) and card 1546: the one failed-region line (RegionLoadState owns it). */
    regionFailed: "Couldn't load this.",

    /** Q-506 card pattern-2 (Q-509 a): the offline region line (RegionLoadState owns it). */
    regionOffline: "You're offline.",

    /** Q-506 card pattern-2 (Q-509 a) and card 1546: the one action under a failed region. */
    regionRetry: 'Try again',

    /**
     * The failed in-place row save (Q-506 card 1524, 1524-b; words final, owner Q-534 a).
     * ONE key; the quiet retry beside it is `regionRetry` (RegionTryAgain).
     */
    rowSaveFailed: "Couldn't save this.",

    /**
     * The answer-key change confirm (Q-506 card 1524 + owner Q-517 a: singular at 1).
     * `count` is the server's `score_change_count`, stated as sent.
     */
    keyChangeScoreCount: (count: number) => `Changes the score for ${count} ${pluralize('person', count)}.`,

    /**
     * The course title confirm (Q-506 card 1524, Q-524 + Q-517 a: singular at 1). `count` is the
     * PacketDetail's `certificate_count`, stated as sent.
     */
    titleCertificateCount: (count: number) => `Changes the title on ${count} ${pluralize('certificate', count)}.`,

    /** Correlation id shown beneath a load failure so a screenshot alone is
     *  enough to find the matching ops alert. */
    errorReference: (traceId: string) => `Error reference: ${traceId}`,

    /** Specific entity load failure */
    loadItemFailed: (entityType: string) =>
      `Failed to load this ${entityType}. Please try again.`,

    /** Network error */
    networkError:
      'Unable to connect. Please check your internet connection and try again.',

    /** Permission denied */
    accessDenied: 'You do not have permission to view this content.',

    /** Not found */
    notFound: (entityType: string) =>
      `${capitalize(entityType)} not found. It may have been deleted.`,

    /** Session expired */
    sessionExpired: 'Your session has expired. Please sign in again.',

    /** Generic retry prompt */
    retryPrompt: 'Something went wrong. Please try again.',

    /** Timeout error */
    timeout: 'The request timed out. Please try again.',
  },

  /**
   * Search states - for search interfaces
   */
  search: {
    /** Prompt to start searching */
    startTyping: 'Start typing to search...',

    /** Minimum characters required */
    minChars: (min: number) => `Type at least ${min} characters to search`,

    /** No results found */
    noResults: (term: string) => `No results found for "${term}"`,

    /** Suggestions when no results */
    suggestions: 'Try different keywords or check your spelling.',

    /** Recent searches empty */
    noRecentSearches: 'No recent searches',
  },

  /**
   * Upload states - for file upload interfaces
   */
  upload: {
    /** No files selected */
    noFiles: 'No files selected',

    /** Drag and drop prompt */
    dragDrop: 'Drag and drop files here, or click to browse',

    /** Upload in progress */
    uploading: 'Uploading...',

    /** Upload complete */
    complete: 'Upload complete',

    /** Upload failed */
    failed: 'Upload failed. Please try again.',
  },

  /**
   * Form states - for form-related empty states
   */
  form: {
    /** No options available in select */
    noOptions: 'No options available',

    /** No matching options in autocomplete */
    noMatches: 'No matching options',

    /** Create new option prompt */
    createNew: (itemType: string) => `Create new ${itemType}`,

    /** Loading options */
    loadingOptions: 'Loading options...',
  },

  /**
   * Calendar/schedule states
   */
  schedule: {
    /** No events on date */
    noEvents: 'No events scheduled',

    /** No upcoming events */
    noUpcoming: 'No upcoming events',

    /** No past events */
    noPast: 'No past events',

    /** No scheduled invitations */
    noScheduledInvitations: 'There are no scheduled invitations.',
  },

  /**
   * Notification states
   */
  notifications: {
    /** No notifications */
    none: 'No notifications',

    /** All caught up */
    allRead: "You're all caught up!",

    /** No unread notifications */
    noUnread: 'No unread notifications',
  },

  /**
   * Filter sidebar states - for FilterNavigation component
   */
  filter: {
    /** No labels exist yet (no items to attach them to) */
    noLabels: 'No labels yet',

    /** Explanation that labels require items */
    labelsRequireItems: (entityType: string) =>
      `Create your first ${entityType} to start organizing with labels.`,

    /** No custom filters exist */
    noCustomFilters: 'No custom filters',
  },

  /**
   * Analytics table/panel states
   */
  analytics: {
    /** No data found in table */
    noData: 'No data found',

    /** Failed to load analytics data */
    loadFailed: 'Failed to load analytics data',
  },

  /**
   * Dashboard states
   */
  dashboard: {
    /** No items to display in recent list */
    noItems: 'No items to display',

    /** Failed to load dashboard */
    loadFailed: 'Failed to load dashboard data. Please try refreshing the page.',

    /** Problem retrieving packet */
    packetRetrievalError: 'There was a problem retrieving this from the server. Please try again.',

    /** Entity-specific empty messages */
    noContacts: 'No contacts yet',
    noPackets: 'No packets yet',
    noChecklists: 'No checklists yet',
  },
} as const;

export type EmptyStateCategory = keyof typeof emptyStates;
